import api from '../apiClient';

// Pricing & Products domain API modules — price structures, calculator,
// templates, product lines, pricing channels, supplier report templates, and
// the Brand List MRP (formerly Price List) surface plus its standalone
// price-analysis helpers. Split out of the monolithic services/api.js and
// re-exported from there so existing imports keep working. Bodies are verbatim
// from the original file.

// Price Structure API
export const priceStructureAPI = {
  getAll: () => api.get('/price-structures'),
  getById: (id) => api.get(`/price-structures/${id}`),
  getByBrand: (brandId) => api.get(`/price-structures/brand/${brandId}`),
  getByBrandAndMarket: (brandId, marketId) => api.get(`/price-structures/brand/${brandId}/market/${marketId}`),
  getLinkedBudgetStatus: (id) => api.get(`/price-structures/${id}/linked-budget`),
  // Copy one structure to other brands under the same supplier. A copy runs
  // the calculator and the projection cascade per target, so the default 60s
  // timeout is raised the way the other bulk writes here do.
  copyToBrands: (id, data) => api.post(`/price-structures/${id}/copy`, data, { timeout: 300000 }),
  update: (id, data) => api.put(`/price-structures/${id}`, data),
  delete: (id) => api.delete(`/price-structures/${id}`),
  getHistory: (id) => api.get(`/price-structures/${id}/history`),
};

// Calculator API
export const calculatorAPI = {
  calculate: (variables) => api.post('/calculator/calculate', variables),
  save: (data) => api.post('/calculator/save', data),
  saveTemplate: (data) => api.post('/calculator/templates', data),
  loadTemplate: (templateId) => api.get(`/calculator/templates/${templateId}`),
};

// Template API
export const templateAPI = {
  getAll: () => api.get('/templates'),
  getById: (id) => api.get(`/templates/${id}`),
  create: (data) => api.post('/templates', data),
  update: (id, data) => api.put(`/templates/${id}`, data),
  delete: (id) => api.delete(`/templates/${id}`),
};

// Supplier Report Templates API (Master Data). create/update send
// multipart/form-data because they carry sample + mapping file uploads.
export const supplierReportTemplateAPI = {
  getAll: () => api.get('/supplier-report-templates'),
  // Brand/Group pick-list for the Configure wizard (groups + independent brands,
  // each with derived supplier + hasTemplate flag).
  targets: () => api.get('/supplier-report-templates/targets'),
  getById: (id) => api.get(`/supplier-report-templates/${id}`),
  // `fd` is a FormData built by the page (metadata fields + file inputs).
  create: (fd) => api.post('/supplier-report-templates', fd, {
    headers: { 'Content-Type': 'multipart/form-data' },
  }),
  update: (id, fd) => api.put(`/supplier-report-templates/${id}`, fd, {
    headers: { 'Content-Type': 'multipart/form-data' },
  }),
  delete: (id) => api.delete(`/supplier-report-templates/${id}`),
  // Returns a blob; the page turns it into a download.
  downloadFile: (id, fileId) => api.get(`/supplier-report-templates/${id}/file/${fileId}`, {
    responseType: 'blob',
  }),
  // Codified (no-AI) instruction file (markdown), reverse-engineered from the mapping.
  downloadInstructions: (id) => api.get(`/supplier-report-templates/${id}/instructions`, {
    responseType: 'blob',
  }),
  // Configure wizard: validate the uploaded Data Source Mapping → engine class / generator.
  mappingStatus: (id) => api.get(`/supplier-report-templates/${id}/mapping-status`),
  // Generate a live preview of the template's output(s) for a period.
  previewGenerate: (id, body) => api.post(`/supplier-report-templates/${id}/preview`, body),
  // Download one generated preview file (blob) by its name.
  downloadPreviewFile: (id, name) => api.get(`/supplier-report-templates/${id}/preview-file`, {
    params: { name }, responseType: 'blob',
  }),
  // Promote the staged draft → live so Sales Reporting uses it.
  implement: (id) => api.post(`/supplier-report-templates/${id}/implement`),
  // Roll the live file set back to a prior version (by history entry id).
  restore: (id, historyId) => api.post(`/supplier-report-templates/${id}/restore/${historyId}`),
  // Drop un-implemented staged changes (on wizard cancel/close).
  discardDraft: (id) => api.post(`/supplier-report-templates/${id}/discard-draft`),
};

// Pricing Channel API (master data) — channels usable in the Price Calculator
export const pricingChannelAPI = {
  getAll: (params) => api.get('/pricing-channels', { params }),
  getById: (id) => api.get(`/pricing-channels/${id}`),
  create: (data) => api.post('/pricing-channels', data),
  update: (id, data) => api.put(`/pricing-channels/${id}`, data),
  delete: (id, { hard = false } = {}) =>
    api.delete(`/pricing-channels/${id}${hard ? '?hard=true' : ''}`)
};

export const productLineAPI = {
  // CRUD
  getAll: (params) => api.get('/product-lines', { params }),
  getByBrand: (brandId) => api.get(`/product-lines/brand/${brandId}`),
  getById: (id) => api.get(`/product-lines/${id}`),
  create: (data) => api.post('/product-lines', data),
  update: (id, data) => api.put(`/product-lines/${id}`, data),
  delete: (id) => api.delete(`/product-lines/${id}`),

  // Filters & Stats
  getFilterOptions: (brandId) => api.get('/product-lines/filters', { params: { brand: brandId } }),
  getStats: (brandId) => api.get('/product-lines/stats', { params: { brand: brandId } })
};

// Brand List MRP API (renamed from "Price List"). All paths target the new
// /brand-list-mrp mount; the backend also keeps /brand-list-mrp mounted as an
// alias. `priceListAPI` is re-exported at the bottom of this object for any
// callers not yet migrated to the new name.
export const brandListMrpApi = {
  upload: (supplierId, brandId, file, uploadedBy = 'System') => {
    const formData = new FormData();
    formData.append('file', file);
    if (supplierId) formData.append('supplierId', supplierId);
    if (brandId) formData.append('brandId', brandId);
    formData.append('uploadedBy', uploadedBy);

    return api.post('/brand-list-mrp/upload', formData, {
      headers: { 'Content-Type': 'multipart/form-data' }
    });
  },
  getAll: (params) => api.get('/brand-list-mrp', { params }),
  getItems: (params) => api.get('/brand-list-mrp/items', { params }),

  // Unconfirmed MRPs queue — rows across every list that carry a data-quality
  // gap (an undated List MRP holding a real figure, or an importer-flagged
  // anomaly). Same match the list view's counts use.
  // `config` carries the caller's AbortController signal: the queue reloads on
  // every filter keystroke and only the newest response may paint.
  getUnconfirmed: (params, config = {}) =>
    api.get('/brand-list-mrp/unconfirmed', { params, ...config }),
  exportUnconfirmed: (params) =>
    api.get('/brand-list-mrp/unconfirmed/export', { params, responseType: 'blob' }),

  // Price-comparison export — read-only workbook of every priced SKU (or a
  // scoped slice): a Summary sheet of how List / GA / Shopify MRP agree, then
  // the row-level comparison with the Items-table pricing columns. Returns a
  // blob. Scope precedence: brandIds[] (explicit multi-brand selection) →
  // supplierId/brandId dropdowns → whole catalogue. productLine narrows the
  // scope; onlyDiscrepancies filters the data sheet alone (the Summary still
  // describes the whole scope). Distinct from the round-trip price-update
  // export (export-combined).
  exportCatalog: ({ supplierId, brandId, brandIds, productLine, onlyDiscrepancies, signal } = {}) =>
    api.post('/brand-list-mrp/export-catalog',
      {
        supplierId: supplierId || undefined,
        brandId: brandId || undefined,
        brandIds: (Array.isArray(brandIds) && brandIds.length > 0) ? brandIds : undefined,
        productLine: productLine || undefined,
        onlyDiscrepancies: onlyDiscrepancies || undefined,
      },
      // A whole-catalogue export runs a per-brand comparison for ~200 brands; the
      // default axios timeout can trip before it finishes. Give it room.
      { responseType: 'blob', timeout: 300000, signal }),

  // Round-trip price-update TEMPLATE export — the editable workbook with the
  // yellow "Proposed List MRP (BDT)" column. The UI sends exactly one price
  // list id: the re-import (processPriceUpdateFile) resolves a single brand, so
  // a file spanning several could only ever be applied a brand at a time. The
  // endpoint still accepts many, and an empty array still means all, for
  // programmatic callers. exportSingle is one list by id. Both return a blob.
  // Distinct from exportCatalog (read-only snapshot).
  exportCombined: (priceListIds = []) =>
    api.post('/brand-list-mrp/export-combined',
      { priceListIds: Array.isArray(priceListIds) ? priceListIds : [] },
      { responseType: 'blob', timeout: 300000 }),
  exportSingle: (priceListId) =>
    api.get(`/brand-list-mrp/${priceListId}/export`, { responseType: 'blob' }),

  getStatus: (id) => api.get(`/brand-list-mrp/${id}`),
  delete: (id) => api.delete(`/brand-list-mrp/${id}`),
  // Exclude (active false) / include the list's brand (admin): left out of the
  // Dashboard, Update Prices to Shopify and Shopify Stock.
  setActive: (id, active) => api.patch(`/brand-list-mrp/${id}/active`, { active }),
  deleteItem: (itemId) => api.delete(`/brand-list-mrp/items/${itemId}`),
  // Admin: soft-deletes several items as one change-log operation.
  deleteItems: (itemIds) => api.post('/brand-list-mrp/items/bulk-delete', { itemIds }),
  updateItem: (itemId, data) => api.put(`/brand-list-mrp/items/${itemId}`, data),
  deleteHistory: (historyId) => api.delete(`/brand-list-mrp/history/${historyId}`),
  getPricingHistory: (sku, supplierId, brandId) =>
    api.get(`/brand-list-mrp/items/${sku}/history`, {
      params: { sku, supplierId, brandId }
    }),
  downloadTemplate: () => {
    return api.get('/brand-list-mrp/template', { responseType: 'blob' })
      .then(response => {
        const url = window.URL.createObjectURL(new Blob([response.data]));
        const link = document.createElement('a');
        link.href = url;
        link.setAttribute('download', 'price-lists-import-template.xlsx');
        document.body.appendChild(link);
        link.click();
        link.remove();
      });
  },

  // Download the Combined Price Lists template (Stage 1 source-of-truth
  // workbook). Separate from downloadTemplate above, which serves the
  // legacy single-brand price-update template.
  downloadCombinedTemplate: () => {
    return api.get('/brand-list-mrp/templates/combined', { responseType: 'blob' })
      .then(response => {
        const url = window.URL.createObjectURL(new Blob([response.data]));
        const link = document.createElement('a');
        link.href = url;
        link.setAttribute('download', 'combined-price-lists-template.xlsx');
        document.body.appendChild(link);
        link.click();
        link.remove();
        window.URL.revokeObjectURL(url);
      });
  },

  // Import Combined Price Lists Excel as source of truth.
  // file: File; dryRun: boolean; replaceMode: 'hard-delete' | 'soft-delete' | 'keep'.
  // Default is 'keep' (upsert-only) — the Import Source modal hard-codes
  // this; the parameter remains for programmatic callers that need the
  // older archive / delete semantics.
  importCombinedList: (file, { dryRun = true, replaceMode = 'keep', uploadedBy } = {}) => {
    const formData = new FormData();
    formData.append('file', file);
    formData.append('dryRun', String(dryRun));
    formData.append('replaceMode', replaceMode);
    if (uploadedBy) formData.append('uploadedBy', uploadedBy);
    // A full-master run writes ~9,000 rows and takes several minutes; the
    // client default of 60s aborts mid-write and reports a failure while the
    // server completes. nginx proxy_read_timeout must allow the same.
    return api.post('/brand-list-mrp/import-combined-list', formData, {
      headers: { 'Content-Type': 'multipart/form-data' },
      timeout: 900000
    });
  },

  // =====================================================
  // PRICE INSIGHTS (Stage 1 Analytics)
  // =====================================================

  // Timeline series. brandId required; sku optional — when present, returns
  // per-SKU exact history points instead of the per-brand weighted average.
  getInsightsTimeline: ({ brandId, sku } = {}) =>
    api.get('/brand-list-mrp/insights/timeline', { params: { brandId, sku } }),

  // Current list MRP vs latest ProductSale MRP per SKU for one brand.
  getInsightsComparison: ({ brandId } = {}) =>
    api.get('/brand-list-mrp/insights/comparison', { params: { brandId } }),

  // Dashboard tab: every brand's per-SKU MRP figures (server-cached ~10 min;
  // refresh recomputes), and the MRP-over-time series for a filter set.
  getDashboardRows: ({ refresh = false } = {}, config = {}) =>
    api.get('/brand-list-mrp/insights/dashboard/rows', {
      params: refresh ? { refresh: 1 } : {},
      timeout: 300000,
      ...config,
    }),
  getDashboardTimeline: (params = {}, config = {}) =>
    api.get('/brand-list-mrp/insights/dashboard/timeline', { params, timeout: 300000, ...config }),

  // Bulk SKU-history for sparklines in the Items modal. One call returns
  // last-N history points for every requested SKU, keyed by SKU.
  // skus: array of SKU strings (joined to a comma-separated query param).
  // limit: optional, default 12 points per SKU.
  getInsightsSkuHistory: ({ brandId, skus, limit } = {}) => {
    const params = { brandId };
    if (skus && skus.length > 0) params.skus = skus.join(',');
    if (limit) params.limit = limit;
    return api.get('/brand-list-mrp/insights/sku-history', { params });
  },

  // Set a List MRP on an item that has none. Two modes (admin-only, SKU trend
  // modal): omit manualMrpBDT to apply the server-computed estimate (purchase ×
  // the brand's sell-out coefficient), or pass a positive manualMrpBDT to set an
  // admin-typed value. Either way the server writes a 'list' price-history row.
  // effectiveDate (DD/MM/YYYY) is optional: supplied, it dates the row and takes
  // it off the Unconfirmed MRPs queue; omitted, the row keeps a price with no
  // date, which is what the queue is there to chase.
  applyCalculatedMrp: (itemId, manualMrpBDT, effectiveDate) =>
    api.patch(`/brand-list-mrp/items/${itemId}/apply-calculated-mrp`, {
      ...(manualMrpBDT != null ? { manualMrpBDT } : {}),
      ...(effectiveDate ? { effectiveDate } : {}),
    }),

  // Delete a single override history row (List or GA/Sales MRP). The server
  // recomputes the item's current value from the remaining rows.
  deleteOverrideHistory: (historyId) =>
    api.delete(`/brand-list-mrp/override-history/${historyId}`),

  // =====================================================
  // SHOPIFY PRICE MIRROR (read-only comparison)
  // =====================================================

  // Last sync + mirror freshness, for the "synced X ago" label + coverage.
  getShopifyStatus: () => api.get('/shopify/status'),

  // Update list prices — table editor. Same writes as the Excel round trip
  // (import tier). commit false previews, true applies.
  applyPriceEdits: (brandId, { items, effectiveDate, commit = false }) =>
    api.post(`/brand-list-mrp/brands/${brandId}/price-edits`, { items, effectiveDate, commit }, { timeout: 300000 }),
  getLastPriceChanges: (brandId) => api.get(`/brand-list-mrp/brands/${brandId}/price-edits/last-changes`),
  getPriceEditLog: (brandId, limit = 5000) =>
    api.get(`/brand-list-mrp/brands/${brandId}/price-edits/log`, { params: { limit } }),
  revertPriceEdits: (brandId, historyIds) =>
    api.post(`/brand-list-mrp/brands/${brandId}/price-edits/revert`, { historyIds }, { timeout: 300000 }),

  // Update Prices to Shopify (admin). preview reads Shopify; push writes only
  // when the server has SHOPIFY_PUSH_ENABLED=true and write_products.
  getShopifyPushStatus: () => api.get('/brand-list-mrp/shopify-push/status'),
  previewShopifyPush: (brandId, { targets, skus }) =>
    api.post(`/brand-list-mrp/brands/${brandId}/shopify-push/preview`, { targets, skus }, { timeout: 300000 }),
  pushToShopify: (brandId, { targets, skus, effectiveDate }) =>
    api.post(`/brand-list-mrp/brands/${brandId}/shopify-push`, { targets, skus, effectiveDate }, { timeout: 300000 }),
  getShopifyPushLog: (brandId) => api.get(`/brand-list-mrp/brands/${brandId}/shopify-push/log`),
  // Re-read one brand's Shopify prices and save them to BI's copy.
  syncShopifyBrand: (brandId) =>
    api.post(`/brand-list-mrp/brands/${brandId}/shopify-push/sync-brand`, {}, { timeout: 300000 }),

  // Admin-only on-demand pull. syncGuard cooldown returns 409 if too soon.
  // A run walks the whole Shopify catalogue — ~90s at 19.5k variants — so the
  // client's 60s default aborts a request the server then completes, and the
  // caller reports a failed sync that in fact succeeded.
  // The body must be {} and not null: the client sets Content-Type
  // application/json globally, so axios serialises null to the literal "null",
  // which express.json rejects in strict mode before the route is reached.
  syncShopify: () => api.post('/shopify/sync', {}, { timeout: 300000 }),

  // =====================================================
  // INVENTORY MANAGEMENT
  // =====================================================

  // Get inventory status with alerts
  getInventoryStatus: (params) => api.get('/brand-list-mrp/inventory/status', { params }),

  // Update inventory config for a single item
  updateInventoryConfig: (itemId, data) =>
    api.put(`/brand-list-mrp/items/${itemId}/inventory-config`, data),

  // Update stock level for a single item
  updateStock: (itemId, quantity, adjustmentType = 'set') =>
    api.put(`/brand-list-mrp/items/${itemId}/stock`, { quantity, adjustmentType }),

  // Bulk update inventory settings
  bulkUpdateInventory: (items) =>
    api.post('/brand-list-mrp/inventory/bulk-update', { items })
};

// Back-compat alias for callers still importing the old name. Same object.
export const priceListAPI = brandListMrpApi;

// Get pricing history for a SKU
export const getPricingHistory = (sku, supplierId, brandId) => {
  return api.get(`/brand-list-mrp/items/${sku}/history`, {
    params: {
      sku: sku,
      supplierId: supplierId,
      brandId: brandId
    }
  });
};

// Round-trip price update: the Export workbook filled in and sent back.
// `commit` false previews the changes, true applies them.
export const uploadPriceUpdates = (formData, config = {}) => {
  return api.post('/brand-list-mrp/brands/upload-updates', formData, {
    headers: {
      'Content-Type': 'multipart/form-data'
    },
    ...config
  });
};
